import { useState, useEffect, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import {
  copyLayoutTemplateAllThemes,
  getAdminFuneralHomes,
  getAdminFuneralHomePageSets,
} from '@/api/admin'
import { Dropdown, FullscreenModal } from '@/components/global'
import Flex from '@/components/priority/Flex/Flex'
import { Button, Input } from '@/components/priority'
import Text from '@/components/priority/Text/Text'
import { getAllProducts } from '@/api/products'
import { applicationTypeAtom } from '@/atoms'
import { getThemeCategories } from '@/api/themes'
import { getCustomPageSets } from '@/api/pageSets'
import { getErrorMessage } from '@/utils/global/getErrorMessage'
import { LaunchCopyOutcome } from './LaunchCopyOutcome'

export const LayoutTemplateModal = ({ show }) => {
  const close = show[1]
  const applicationType = useRecoilValue(applicationTypeAtom)
  const [copyMode, setCopyMode] = useState('global')
  const [themes, setThemes] = useState([])
  const [products, setProducts] = useState([])
  const [layouts, setLayouts] = useState([])
  const [selectedTheme, setSelectedTheme] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedLayout, setSelectedLayout] = useState('')
  const [applyToTheme, setApplyToTheme] = useState('')
  const [loading, setLoading] = useState(false)
  const [refusal, setRefusal] = useState(null)

  // FH mode state
  const [funeralHomes, setFuneralHomes] = useState([])
  const [selectedFuneralHome, setSelectedFuneralHome] = useState('')
  const [funeralHomeSearch, setFuneralHomeSearch] = useState('')

  const resetState = () => {
    setSelectedTheme('')
    setSelectedProduct('')
    setSelectedLayout('')
    setApplyToTheme('')
    setSelectedFuneralHome('')
    setFuneralHomeSearch('')
    setRefusal(null)
  }

  const filteredFuneralHomes = useMemo(() => {
    const decorated = funeralHomes.map((fh) => ({
      ...fh,
      displayLabel: `${fh.name} - ${fh.id}`,
    }))
    if (!funeralHomeSearch) return decorated
    const query = funeralHomeSearch.toLowerCase()
    return decorated.filter((fh) =>
      fh.displayLabel.toLowerCase().includes(query),
    )
  }, [funeralHomes, funeralHomeSearch])

  // Fetch funeral homes when FH mode is selected
  useEffect(() => {
    if (copyMode !== 'funeralHome') return

    const fetchFuneralHomes = async () => {
      try {
        const response = await getAdminFuneralHomes()
        setFuneralHomes(response.data)
      } catch (error) {
        console.error('Failed to fetch funeral homes:', error)
      }
    }

    fetchFuneralHomes()
  }, [copyMode])

  // Reset selections when mode changes
  useEffect(() => {
    resetState()
  }, [copyMode])

  // A layout belongs to the home it was listed for.
  useEffect(() => {
    setSelectedLayout('')
    setLayouts([])
  }, [selectedFuneralHome])

  // A layout belongs to the product and theme it was listed for.
  useEffect(() => {
    setSelectedLayout('')
    setApplyToTheme('')
  }, [selectedProduct, selectedTheme])

  // Fetch products on mount — product is selected first, then theme.
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getAllProducts()
        const sortedProducts = response.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setProducts(sortedProducts)
      } catch (error) {
        console.error('Failed to fetch products:', error)
      }
    }

    fetchProducts()
  }, [])

  // Fetch themes when a product is selected. Backend determines if the
  // product is M3 or MDL (via m3_products vs products lookup) and returns
  // only themes whose application matches.
  useEffect(() => {
    if (!selectedProduct) {
      setThemes([])
      return
    }
    const fetchThemes = async () => {
      try {
        const response = await getThemeCategories({
          productId: selectedProduct,
        })
        const sortedThemes = response.data[0].themes.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setThemes(sortedThemes)
      } catch (error) {
        console.error('Failed to fetch themes:', error)
      }
    }

    fetchThemes()
  }, [selectedProduct])

  useEffect(() => {
    const fetchPageSets = async () => {
      if (!selectedTheme || !selectedProduct) return

      try {
        let response
        if (copyMode === 'funeralHome' && selectedFuneralHome) {
          response = await getAdminFuneralHomePageSets(
            selectedFuneralHome,
            selectedProduct,
            selectedTheme,
            applicationType,
          )
        } else if (copyMode === 'global') {
          response = await getCustomPageSets(
            selectedProduct,
            selectedTheme,
            undefined,
            applicationType,
          )
        } else {
          return
        }
        const sortedLayouts = response.data.sort((a, b) =>
          a.name.localeCompare(b.name),
        )
        setLayouts(sortedLayouts)
      } catch (error) {
        console.error('Failed to fetch page sets:', error)
      }
    }

    fetchPageSets()
  }, [
    selectedTheme,
    selectedProduct,
    applicationType,
    copyMode,
    selectedFuneralHome,
  ])

  const handleSubmit = async () => {
    if (!selectedTheme || !selectedProduct || !selectedLayout) return

    setLoading(true)
    setRefusal(null)
    try {
      await copyLayoutTemplateAllThemes({
        pageSetId: selectedLayout,
        productId: selectedProduct,
        themeId: selectedTheme,
        applyToTheme: applyToTheme || undefined,
        funeralHomeId:
          copyMode === 'funeralHome' ? selectedFuneralHome : undefined,
      })
      resetState()
      close()
    } catch (error) {
      setRefusal(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    resetState()
    close()
  }

  const isFuneralHomeMode = copyMode === 'funeralHome'
  // With no target home, a funeral home's layout overwrites the same-named
  // layouts of every funeral home.
  const copiesAcrossHomes =
    !isFuneralHomeMode &&
    layouts.find((layout) => layout.id === selectedLayout)?.type ===
      'FUNERAL_HOME'
  const isDisabled =
    !selectedTheme ||
    !selectedProduct ||
    !selectedLayout ||
    (isFuneralHomeMode && !selectedFuneralHome) ||
    loading

  return (
    <FullscreenModal show={show} hideClose>
      <Flex
        column
        padding="32px"
        width="500px"
        maxHeight="90vh"
        justify="space-between"
      >
        <Flex column>
          <Text size="24px" weight="600" margin="0 0 20px 0">
            Copy Layout Template
          </Text>
          <Text margin="0 0 20px 0">
            Select a theme/product combo to view the associated layouts. Once
            you have selected the layout, click on apply to copy the layout
            json_template to all associated product themes.
          </Text>
          <Text margin="0 0 20px 0" color="red">
            This will permanently override any existing themes layouts with
            similar names
          </Text>
          <Text
            margin="0 0 20px 0"
            color="red"
            data-testid="layout-copy-warning"
          >
            Layouts are matched by name and product, never by language. A
            same-named layout in another language is overwritten too, and keeps
            its own language, name and default setting. Pages are replaced in
            order; if a target has more pages than this layout, its extra pages
            keep their old content, and a target with fewer pages takes only
            that many of this layout's pages.
          </Text>
          <Flex margin="0 0 24px 0">
            <Button
              primary={!isFuneralHomeMode}
              margin="0 8px 0 0"
              onClick={() => setCopyMode('global')}
            >
              Global
            </Button>
            <Button
              primary={isFuneralHomeMode}
              onClick={() => setCopyMode('funeralHome')}
            >
              Funeral Home
            </Button>
          </Flex>
          {isFuneralHomeMode && (
            <>
              <Input
                width="100%"
                margin="0 0 12px 0"
                value={[funeralHomeSearch, setFuneralHomeSearch]}
                placeholder="Search funeral homes..."
              />
              <Dropdown
                width="100%"
                margin="0 0 24px 0"
                color="inherit"
                data-testid="layout-copy-funeral-home"
                selected={[selectedFuneralHome, setSelectedFuneralHome]}
                placeholder="Select a funeral home"
                items={filteredFuneralHomes}
                valueKey="id"
                textKey="displayLabel"
                full={true}
              />
            </>
          )}
          <Dropdown
            width="100%"
            margin="0 0 24px 0"
            color="inherit"
            data-testid="layout-copy-product"
            selected={[selectedProduct, setSelectedProduct]}
            placeholder="Select a product"
            items={products}
            valueKey="id"
            textKey="name"
            full={true}
          />
          {selectedProduct && (
            <Dropdown
              width="100%"
              margin="0 0 24px 0"
              color="inherit"
              data-testid="layout-copy-theme"
              selected={[selectedTheme, setSelectedTheme]}
              placeholder="Select a theme"
              items={themes}
              valueKey="id"
              textKey="name"
              full={true}
            />
          )}
          {selectedTheme && (
            <Dropdown
              width="100%"
              margin="0 0 24px 0"
              color="inherit"
              data-testid="layout-copy-layout"
              selected={[selectedLayout, setSelectedLayout]}
              placeholder="Select an associated layout"
              items={layouts}
              valueKey="id"
              textKey="name"
              full={true}
            />
          )}
          {copiesAcrossHomes && (
            <Text
              margin="0 0 20px 0"
              color="red"
              data-testid="layout-copy-cross-home-warning"
            >
              This also overwrites other funeral homes' layouts with the same
              name and product, and creates none in a theme that lacks one.
            </Text>
          )}
          {selectedLayout && (
            <>
              <Text
                margin="0 0 12px 0"
                color="orange"
                data-testid="layout-copy-apply-to-hint"
              >
                Select an item below if you only want to apply this to a
                specific theme - layout combo. Otherwise, this will overwrite
                all theme layouts with the same name
                {copiesAcrossHomes
                  ? '.'
                  : ", or if the layout doesn't exist for a theme it will be created with the same name."}
              </Text>
              <Dropdown
                width="100%"
                margin="0 0 24px 0"
                color="inherit"
                selected={[applyToTheme, setApplyToTheme]}
                placeholder="Optionally select a specific theme to apply to"
                items={themes}
                valueKey="id"
                textKey="name"
                full={true}
              />
            </>
          )}
          <LaunchCopyOutcome refusal={refusal} skipped={[]} />
        </Flex>
        <Flex justify="flex-end" margin="24px 0 0 0">
          <Button margin="0 16px 0 0" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            primary
            left
            margin="0 12px 0 0"
            onClick={handleSubmit}
            disabled={isDisabled}
            opacity={isDisabled ? 0.5 : 1}
            pointerEvents={isDisabled ? 'none' : 'auto'}
          >
            {loading ? 'Copying...' : 'Apply layout template to all themes'}
          </Button>
        </Flex>
      </Flex>
    </FullscreenModal>
  )
}
